import React, { useMemo } from 'react';
import Select from 'react-select';
import './TextField.css';
import { FaLock } from 'react-icons/fa6';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import { isLooseOptionSelected, resolveSelectValue } from './reactSelectHelpers';

interface Option {
    value: string | number;
    label: string;
}

interface Props {
    labelClass?: string;
    leftLabel?: string;
    required?: boolean;
    label?: string;
    selectClass?: string;
    onChange?: (selectedOption: Option | null) => void;
    disabled?: boolean;
    readOnly?: boolean;
    onBlur?: () => void;
    value?: Option | null;
    name?: string;
    options?: Option[];
    placeholder?: string;
    customLabel?: string;
    customLabelClick?: any;
    customLabelClass?: any;
    getOptionLabel?: any;
    mainClass?: any;
    errorMsg?: any;
    isClearable?: boolean; // Add this prop
    isSearchable?: boolean; // Add this for better UX
    customLabelTooltip?: string;
    menuPortalTarget?: HTMLElement | null;
    menuPosition?: 'fixed' | 'absolute';
    styles?: any;
    [key: string]: any;
}

const SearchSelect = ({
    labelClass,
    leftLabel,
    required,
    label,
    selectClass,
    onChange,
    disabled,
    readOnly,
    name,
    onBlur,
    value,
    options,
    placeholder,
    customLabel,
    customLabelClick,
    customLabelClass,
    getOptionLabel,
    mainClass,
    errorMsg,
    isClearable = false, // Default to false
    isSearchable = true, // Default to true
    customLabelTooltip,
    ...fieldProps
}: Props) => {
    const isFieldDisabled = Boolean(disabled || (fieldProps as any)?.readOnly);

    const resolvedValue = useMemo(
        () => resolveSelectValue(value, options),
        [value, options]
    );

    const customStyles = useMemo(() => {
        const parentStyles = (fieldProps as any)?.styles || {};
        const baseStyles = {
            control: (base: any, state: any) => ({
                ...base,
                minHeight: '38px',
                height: '38px',
                display: 'flex',
                alignItems: 'center',
                ...(parentStyles.control ? parentStyles.control(base, state) : {}),
            }),
            valueContainer: (base: any, state: any) => ({
                ...base,
                height: '100%',
                padding: '0 8px',
                display: 'flex',
                alignItems: 'center',
                position: 'relative',
                ...(parentStyles.valueContainer ? parentStyles.valueContainer(base, state) : {}),
            }),
            placeholder: (base: any, state: any) => ({
                ...base,
                margin: 0,
                position: 'absolute',
                top: '50%',
                transform: 'translateY(-50%)',
                ...(parentStyles.placeholder ? parentStyles.placeholder(base, state) : {}),
            }),
            singleValue: (base: any, state: any) => ({
                ...base,
                margin: 0,
                position: 'absolute',
                top: '50%',
                transform: 'translateY(-50%)',
                ...(parentStyles.singleValue ? parentStyles.singleValue(base, state) : {}),
            }),
            indicatorsContainer: (base: any, state: any) => ({
                ...base,
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                ...(parentStyles.indicatorsContainer ? parentStyles.indicatorsContainer(base, state) : {}),
            }),
        };

        if (!isFieldDisabled) {
            return {
                ...parentStyles,
                ...baseStyles,
            };
        }

        return {
            ...parentStyles,
            ...baseStyles,
            control: (base: any, state: any) => ({
                ...baseStyles.control(base, state),
                backgroundColor: '#f5f8fa',
                borderColor: '#a1a5b7',
                cursor: 'not-allowed',
                pointerEvents: 'auto',
            }),
            valueContainer: (base: any, state: any) => ({
                ...baseStyles.valueContainer(base, state),
                backgroundColor: 'transparent',
                cursor: 'not-allowed',
            }),
            singleValue: (base: any, state: any) => ({
                ...baseStyles.singleValue(base, state),
                cursor: 'not-allowed',
            }),
            placeholder: (base: any, state: any) => ({
                ...baseStyles.placeholder(base, state),
                cursor: 'not-allowed',
            }),
            dropdownIndicator: (base: any, state: any) => ({
                ...base,
                ...(parentStyles.dropdownIndicator ? parentStyles.dropdownIndicator(base, state) : {}),
                cursor: 'not-allowed',
            }),
        };
    }, [isFieldDisabled, (fieldProps as any)?.styles]);

    // const sortedOptions = options?.slice().sort((a, b) => a.label.localeCompare(b.label));
    const sortedOptions = options?.slice().sort((a, b) => {
        const regex = /^\d+/; // Regex to match leading numbers

        const numA = a.label.match(regex);
        const numB = b.label.match(regex);

        if (numA && numB) {
            // Both labels start with numbers, compare numerically
            return parseInt(numA[0]) - parseInt(numB[0]);
        } else if (numA) {
            // A has a number, B does not -> A should come first
            return -1;
        } else if (numB) {
            // B has a number, A does not -> B should come first
            return 1;
        }

        // If neither label starts with a number, sort alphabetically
        return a.label.localeCompare(b.label);
    });

    return (
        <>
            <Tooltip id={`lock-tooltip`} place="top" />
            <div className={`${mainClass ? `${mainClass} ` : ''}${leftLabel ? 'd-md-flex w-100 gap-2' : 'w-100'}${isFieldDisabled ? ' ss-field-readonly-wrap' : ''}`.trim()}>
                {label &&
                    <div className='d-flex justify-content-between align-items-center'>
                        <label className={`form-label fw-bold text-dark fs-6 text-nowrap ${labelClass || ''}`.trim()}>
                            {label}{leftLabel}
                            {required && <span className="required-star text-danger ms-1">*</span>}
                            {isFieldDisabled &&
                                <>
                                    <FaLock className='fs-7 ms-1' /> <i className="fa fa-info-circle ms-2 cursor-pointer"
                                        data-tooltip-id={`lock-tooltip`}
                                        data-tooltip-content='This field is locked and cannot be modified'></i>
                                </>}
                        </label>
                        <label
                            className={`form-label fw-bold text-primary text-nowrap ${customLabelClass || ''}`.trim()}
                            onClick={customLabelClick}
                            style={{ fontSize: customLabelTooltip ? '12px' : '10px' }}
                            title={customLabelTooltip}
                            aria-label={typeof customLabel === 'string' ? customLabel : undefined}
                            data-tooltip-id={customLabelTooltip ? 'ss-search-select-custom-label' : undefined}
                            data-tooltip-content={customLabelTooltip}
                        >
                            {customLabel}
                        </label>
                        {customLabelTooltip && (
                            <Tooltip id="ss-search-select-custom-label" place="top" positionStrategy="fixed" />
                        )}
                    </div>
                }
                <Select
                    className={`${selectClass ? `${selectClass} ` : ''}${isFieldDisabled ? 'ss-field-readonly' : ''}`.trim()}
                    classNamePrefix='react-select'
                    onChange={onChange}
                    isDisabled={isFieldDisabled}
                    onBlur={onBlur}
                    value={resolvedValue}
                    name={name}
                    options={options}
                    isOptionSelected={isLooseOptionSelected}
                    // options={sortedOptions}
                    placeholder={placeholder}
                    getOptionLabel={getOptionLabel}
                    isClearable={isClearable}
                    isSearchable={isSearchable}
                    {...fieldProps}
                    styles={customStyles}
                />
                {errorMsg ? <p className='text-danger mb-0 mt-1'>{errorMsg}</p> : null}
            </div>
        </>
    );
};

export default SearchSelect;